import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  IRhiV2ErrorCopy,
  IRhiV2ErrorScreenState,
  RHI_V2_FRESH_QUOTE_LABEL,
} from '@/constants/rhiV2ErrorCopy';
import { isAgentLaunch } from '@/utils/agentSession';
import {
  isRhiV2QuoteExpired,
  isRhiV2QuoteUnpriceable,
  resolveRhiV2Error,
} from '@/utils/rhiV2Error';
import {
  RHI_V2_AGENT_PATH,
  RHI_V2_ERROR_PATH,
  RHI_V2_NEW_QUOTE_PATH,
} from '@/utils/rhiV2/journey';

/**
 * Sends the user to the RHI error screen with the copy for this error, or the copy given.
 * The screen's "Try again" reopens the page the error happened on, which loads afresh;
 * an expired quote offers "Get fresh quotes" instead.
 */
export const useShowRhiV2Error = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  return useCallback(
    (error: unknown, copy?: IRhiV2ErrorCopy): IRhiV2ErrorCopy => {
      const shown = copy ?? resolveRhiV2Error(error);
      // Already showing an error (an effect re-running as the URL changed): keep the page
      // Try again goes back to.
      if (pathname === RHI_V2_ERROR_PATH) return shown;

      // An expired quote, or one whose add-on choices were erased, never comes back: offer a
      // fresh quote instead of Try again.
      const state: IRhiV2ErrorScreenState =
        isRhiV2QuoteExpired(error) || isRhiV2QuoteUnpriceable(error)
        ? {
            error: shown,
            action: {
              label: RHI_V2_FRESH_QUOTE_LABEL,
              to: isAgentLaunch() ? RHI_V2_AGENT_PATH : RHI_V2_NEW_QUOTE_PATH,
            },
          }
        : { error: shown, retryTo: `${pathname}${search}` };

      navigate(RHI_V2_ERROR_PATH, { replace: true, state });

      return shown;
    },
    [navigate, pathname, search],
  );
};

export default useShowRhiV2Error;
